import { getAthenaVersionInfo } from '../config/version';

/**
 * Diagnóstico compartilhado de voz, somente em memória.
 *
 * O gravador nunca aceita ou persiste transcript, texto, áudio, prompt,
 * mensagem, payload, token ou URL. Ele existe para investigar ciclo de vida,
 * permissões e provedores sem transformar a conversa em telemetria.
 */

export const MAX_VOICE_DIAGNOSTIC_EVENTS = 200;

export const VOICE_DIAGNOSTIC_EVENTS = Object.freeze({
  RECORDING_START: 'recordingStart',
  SPEECH_START: 'speechStart',
  SPEECH_END: 'speechEnd',
  TURN_END: 'turnEnd',
  STT_START: 'sttStart',
  STT_RESULT: 'sttResult',
  TTS_REQUEST: 'ttsRequest',
  TTS_PLAYBACK: 'ttsPlayback',
  BARGE_IN: 'bargeIn',
  CANCEL: 'cancel',
  ERROR: 'error',
});

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const listeners = new Set();
const events = [];
let sequence = 0;
let context = {};

// A allowlist é intencionalmente pequena. Não fazemos spread de `details`,
// portanto campos de conteúdo (transcript, audio, text etc.) são descartados.
const STRING_FIELDS = Object.freeze({
  platform: 24,
  device: 96,
  androidVersion: 32,
  provider: 64,
  permission: 48,
  sessionId: 120,
  turnId: 120,
  context: 48,
  state: 48,
  errorCode: 96,
  lifecycle: 48,
  source: 48,
});

const BOOLEAN_FIELDS = Object.freeze([
  'native',
  'microphone',
  'stt',
  'continuous',
  'bargeIn',
  'tts',
]);

const NUMBER_FIELDS = Object.freeze({
  durationMs: [0, 60 * 60 * 1000],
  attempt: [0, 100],
  status: [0, 999],
  apiStatus: [0, 999],
});

function cleanString(value, maxLength) {
  if (typeof value !== 'string') return undefined;
  // Dados diagnósticos são identificadores/labels, nunca texto de conversa.
  // Caracteres de controle e quebras de linha são removidos para evitar que um
  // valor inesperado carregue uma mensagem disfarçada de metadado.
  const normalized = value.replace(/[\r\n\t\u0000-\u001f]/g, ' ').trim();
  if (!normalized) return undefined;
  return normalized.slice(0, maxLength);
}

function cleanEventName(value) {
  if (typeof value !== 'string') return 'unknown';
  const normalized = value.trim();
  return /^[A-Za-z][A-Za-z0-9._:-]{0,63}$/.test(normalized) ? normalized : 'unknown';
}

function sanitizeDetails(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return {};
  const safe = {};

  Object.entries(STRING_FIELDS).forEach(([field, maxLength]) => {
    if (!hasOwn(details, field)) return;
    const value = cleanString(details[field], maxLength);
    if (value !== undefined) safe[field] = value;
  });

  BOOLEAN_FIELDS.forEach((field) => {
    if (hasOwn(details, field) && typeof details[field] === 'boolean') safe[field] = details[field];
  });

  Object.entries(NUMBER_FIELDS).forEach(([field, [minimum, maximum]]) => {
    if (!hasOwn(details, field)) return;
    const value = details[field];
    if (typeof value === 'number' && Number.isFinite(value) && value >= minimum && value <= maximum) {
      safe[field] = value;
    }
  });

  return safe;
}

function copyEntry(entry) {
  return {
    ...entry,
    version: { ...entry.version },
  };
}

function emit(entry) {
  listeners.forEach((listener) => {
    try {
      listener(copyEntry(entry));
    } catch {
      // Um consumidor de diagnóstico nunca pode interromper a sessão de voz.
    }
  });
}

/**
 * Define metadados não sensíveis que serão anexados aos eventos seguintes.
 * Passar `null` a um campo permitido o remove do contexto atual.
 */
export function setVoiceDiagnosticsContext(nextContext = {}) {
  if (!nextContext || typeof nextContext !== 'object' || Array.isArray(nextContext)) {
    return { ...context };
  }

  const next = { ...context };
  const allowedFields = [
    ...Object.keys(STRING_FIELDS),
    ...BOOLEAN_FIELDS,
    ...Object.keys(NUMBER_FIELDS),
  ];

  allowedFields.forEach((field) => {
    if (!hasOwn(nextContext, field)) return;
    if (nextContext[field] === null) {
      delete next[field];
      return;
    }
    const sanitized = sanitizeDetails({ [field]: nextContext[field] });
    if (hasOwn(sanitized, field)) next[field] = sanitized[field];
  });

  context = Object.freeze(next);
  return { ...context };
}

export function clearVoiceDiagnosticsContext() {
  context = {};
}

/**
 * Registra um evento local e retorna uma cópia segura dele.
 * Nenhum evento é enviado à rede, salvo se outro módulo explicitamente optar
 * por exportar o snapshot já higienizado.
 */
export function recordVoiceDiagnostic(event, details = {}) {
  const entry = Object.freeze({
    id: `voice-${Date.now()}-${++sequence}`,
    timestamp: new Date().toISOString(),
    event: cleanEventName(event),
    ...context,
    ...sanitizeDetails(details),
    version: Object.freeze(getAthenaVersionInfo()),
  });

  events.push(entry);
  if (events.length > MAX_VOICE_DIAGNOSTIC_EVENTS) events.splice(0, events.length - MAX_VOICE_DIAGNOSTIC_EVENTS);
  emit(entry);
  return copyEntry(entry);
}

/** Retorna apenas cópias de eventos já sanitizados, do mais antigo ao mais novo. */
export function getVoiceDiagnostics({ limit = MAX_VOICE_DIAGNOSTIC_EVENTS } = {}) {
  const safeLimit = Number.isInteger(limit)
    ? Math.max(0, Math.min(limit, MAX_VOICE_DIAGNOSTIC_EVENTS))
    : MAX_VOICE_DIAGNOSTIC_EVENTS;
  return events.slice(-safeLimit).map(copyEntry);
}

export function clearVoiceDiagnostics() {
  events.splice(0, events.length);
  sequence = 0;
}

/** Assinatura local para uma tela de suporte; não persiste e não envia eventos. */
export function subscribeVoiceDiagnostics(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}
